import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";

import { memo, useState } from "react";
import { ChatMessageWithParts, ImageContent } from "src/views/assistant/types";
import { useChatContext } from "../context/ChatContext";
import UserMessageAvatar from "../../../views/assistant/components/ui/UserMessageAvatar";
import EditTextMenuButtons from "./EditTextMenuButtons";
import UserMessageActions from "./UserMessageActions";
import UserMessageText from "./UserMessageText";

type UserMessageProps = {
  messageGroupIdx: number;
  messageIndex: number;
  dialogue: ChatMessageWithParts[];
  chatMessage: ChatMessageWithParts;
  isTurnActive?: boolean;
  resendUserMessage: (
    id: string,
    text: string,
    images?: ImageContent[],
  ) => void;
};

const UserMessage = ({
  messageGroupIdx,
  messageIndex,
  dialogue,
  chatMessage,
  isTurnActive = false,
  resendUserMessage,
}: UserMessageProps) => {
  const messageText = chatMessage.contentParts[0]?.text || "";
  const PREAMBLE_TEXT_MARKER = "ATOMASSIST_PREAMBLE_GREETING_V1"; // Must match the marker in AtomAssistPage.tsx
  const { transcriptOnly = false, hideUserMessage } = useChatContext();

  const [isEditing, setIsEditing] = useState(false);
  //* For user message can make assumption that first item will always contain user text
  const [editedText, setEditedText] = useState(
    chatMessage.contentParts[0]?.text ?? "",
  );
  // Track edited images - initialize from chatMessage when entering edit mode
  const [editedImages, setEditedImages] = useState<ImageContent[] | undefined>(
    chatMessage.images,
  );

  const handleRemoveImage = (index: number) => {
    setEditedImages((prev) => prev?.filter((_, i) => i !== index));
  };

  // Reset edited images when entering edit mode
  const handleStartEditing = (_isEditing: boolean) => {
    setEditedImages(chatMessage.images);
    setIsEditing(true);
  };

  const handleSaveUserMessage = () => {
    setIsEditing(false);
    resendUserMessage(chatMessage.id || "", editedText, editedImages);
  };

  const theme = useTheme();
  const lessThanMdBreakpoint = useMediaQuery(theme.breakpoints.down("md"));

  // Below every hook, so a line the surface hides keeps its state.
  if (
    messageText.startsWith(PREAMBLE_TEXT_MARKER) ||
    hideUserMessage?.(chatMessage, messageGroupIdx)
  ) {
    return null;
  }

  // NOTE: Do NOT mutate chatMessage.id here. The chatMessage object is shared
  // with the dialogue state that gets sent to the backend. Mutating it during
  // render assigns a frontend UUID that doesn't match the backend-generated ID,
  // which causes duplicate messages in the DB when the next request is sent
  // (the ID mismatch bypasses deduplication). The backend assigns the real ID
  // via the assigned_message_id metadata event; handleSaveUserMessage already
  // guards against missing IDs.

  return (
    <Box
      key={messageIndex}
      id={`chat-bubble-container-${chatMessage?.type}-${messageIndex}`}
      data-testid={`aa-msg-user-${messageGroupIdx}`}
      className={`chat-bubble-container ${chatMessage.type}`}
    >
      <Stack
        direction={lessThanMdBreakpoint ? "column" : "row"}
        gap={2}
        alignItems="flex-start"
        width={"100%"}
      >
        <UserMessageAvatar />
        <Box
          id={`chat-bubble-content-${chatMessage?.type}-${messageIndex}`}
          data-testid={`aa-msg-user-content-${messageGroupIdx}`}
          className="chat-bubble-content"
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            width="100%"
            justifyContent="space-between"
          >
            <Box flex={1} minWidth={0}>
              <UserMessageText
                messageIndex={messageIndex}
                chatMessage={chatMessage}
                isEditing={isEditing}
                editedText={editedText}
                setEditedText={setEditedText}
                handleSaveUserMessage={handleSaveUserMessage}
                dialogueId={dialogue[0].id ? Number(dialogue[0].id) : null}
                editedImages={editedImages}
                onRemoveImage={handleRemoveImage}
              />
              {isEditing && (
                <Box mt={1}>
                  <EditTextMenuButtons
                    chatMessage={chatMessage}
                    dialogue={dialogue}
                    isTurnActive={isTurnActive}
                    setIsEditing={setIsEditing}
                    setEditedText={setEditedText}
                    handleSaveUserMessage={handleSaveUserMessage}
                  />
                </Box>
              )}
            </Box>
            {!transcriptOnly && (
              // Editing re-sends a past answer; interviews only move forward.
              <Box flexShrink={0}>
                <UserMessageActions
                  isEditing={isEditing}
                  setIsEditing={handleStartEditing}
                />
              </Box>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};
export default memo(UserMessage);
